/**
 * Wald-Kopf der Einstellungen (Board „Preisautomatik · Einstellungen“): Apartmentname als Titel,
 * Version und Stand als Chips, Apartmentwahl ‹ ▾ › rechts; Pfad, Bereichsumschalter und Reiter
 * kommen aus dem Gerüst. Ein Wechsel mit offenen Eingaben fragt vorher nach (Aufrufer).
 */
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { PageHeader, forestArrow, forestGroup } from '@/shared/components';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/shared/lib/cn';
import type { ConfigurationApartment } from './useConfigurationApartments';

interface Props {
  title: string;
  chips?: ReactNode;
  apartments: ConfigurationApartment[];
  apartmentId?: string;
  onApartment: (id: string) => void;
}

export function ConfigurationHeader({ title, chips, apartments, apartmentId, onApartment }: Props) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const index = apartments.findIndex((apartment) => apartment.id === apartmentId);
  const step = (delta: number) => {
    const next = apartments[(Math.max(index, 0) + delta + apartments.length) % apartments.length];
    if (next) onApartment(next.id);
  };
  const arrow = (label: string, delta: number, Icon: typeof ChevronLeft) => (
    <button
      type="button"
      aria-label={label}
      disabled={apartments.length < 2 || index < 0}
      onClick={() => step(delta)}
      className={cn(forestArrow, 'disabled:opacity-40')}
    >
      <Icon size={18} aria-hidden />
    </button>
  );
  const apartmentSwitch = (
    <div className={cn(forestGroup, 'relative h-11 min-w-0', mobile && 'grow')}>
      {!mobile && arrow('Vorheriges Apartment', -1, ChevronLeft)}
      <select
        aria-label="Apartment"
        value={apartmentId ?? ''}
        onChange={(event) => onApartment(event.target.value)}
        className="[&>option]:text-ink h-11 min-w-0 grow appearance-none truncate bg-transparent pl-3 pr-8 text-sm font-bold text-white outline-none md:max-w-[13rem] md:pl-1"
      >
        {index < 0 && <option value="">Apartment wählen</option>}
        {apartments.map((apartment) => (
          <option key={apartment.id} value={apartment.id}>
            {apartment.name}
          </option>
        ))}
      </select>
      <ChevronDown
        size={14}
        aria-hidden
        className={cn('pointer-events-none absolute', mobile ? 'right-4' : 'right-12')}
      />
      {!mobile && arrow('Nächstes Apartment', 1, ChevronRight)}
    </div>
  );
  return (
    <PageHeader
      className="pb-[84px] md:pb-[84px]"
      title={title}
      description={chips}
      tools={mobile ? undefined : apartmentSwitch}
      below={mobile ? apartmentSwitch : undefined}
    />
  );
}

/** Chips unter dem Titel: Version mit Gültigkeit und ob Änderungen offen sind. */
export function ConfigurationHeaderChips({ version, dirty }: { version: string; dirty: boolean }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      <span className="rounded-pill flex min-h-7 items-center bg-white/10 px-2.5 text-xs font-bold text-white">
        {version}
      </span>
      <span
        className={cn(
          'rounded-pill flex min-h-7 items-center px-2.5 text-xs font-bold',
          dirty ? 'bg-amber-on-dark text-ink' : 'bg-mint text-forest-deep',
        )}
      >
        {dirty ? 'Änderungen noch nicht gespeichert' : 'Gespeicherter Stand'}
      </span>
    </span>
  );
}
